import React, { useState, useEffect } from "react";
import { List, Set } from "immutable";
import styled, { css } from "styled-components";

import { CleaningProductsType } from "@typings/booking";

import { rem } from "@ui/helpers";
import { TestWrapper } from "@ui/Atoms";
import { CleaningProductSummaryElement } from "@ui/Molecules";

function filterCleaningPlans(
  ids: Set<string>,
  items: CleaningProductsType,
): CleaningProductsType {
  if (!items) {
    return List();
  }

  return items.filter(record => ids.has(record.get("productId")));
}

interface Props {
  testId?: string;
  className?: string;
  cleaningProducts: CleaningProductsType;
  selectedProductsIds: Set<string>;
  planHasProductsSpecialPrice: boolean;
  translations: {
    oneTimeLabelText: string;
  };
}

export const CleaningProductsSummary = (props: Props): React.ReactElement => {
  const [selectedCleaningProducts, setSelectedCleaningProducts] = useState(
    filterCleaningPlans(props.selectedProductsIds, props.cleaningProducts),
  );

  useEffect(() => {
    setSelectedCleaningProducts(
      filterCleaningPlans(props.selectedProductsIds, props.cleaningProducts),
    );
  }, [props.cleaningProducts, props.selectedProductsIds]);

  return (
    <TestWrapper testId={props.testId}>
      <ProductsList className={props.className}>
        {selectedCleaningProducts?.map(record => (
          <CleaningProductSummaryElement
            key={`booking-summary-cp-${record.get("productId")}`}
            productId={record.get("productId")}
            name={record.get("name")}
            description={record.get("description")}
            imageUrl={record.get("imageUrl")}
            regularPrice={record.get("regularPrice")}
            specialPrice={record.get("specialPrice")}
            isSpecialPricePlanSelected={props.planHasProductsSpecialPrice}
            currency={record.get("currency")}
            oneTimeLabelText={props.translations.oneTimeLabelText}
            removeCurrency
          />
        ))}
      </ProductsList>
    </TestWrapper>
  );
};

const ProductsList = styled.ul`
  margin: ${props => props.theme.margins.base_x2} 0 0;
  padding: ${props => props.theme.margins.base_x2} 0 0;
  border-top: ${rem(1)} solid ${props => props.theme.palette.grey100};
  list-style: none;

  ${props => props.theme.breakpoints.desktop} {
    margin-top: ${props => props.theme.margins.base};
  }

  > li {
    &:first-child {
      padding-top: 0;
    }
  }
`;
